import { useState } from "react";
import { Copy, Check, ExternalLink, Info, Film } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { VideoMeta } from "@/types/video";

interface VideoPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  video: (VideoMeta & { id: string }) | null;
  dramaId: string;
  dramaTitle?: string;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * 集數預覽彈窗。
 *
 * 目前 CMS 不直接嵌入 BytePlus 播放器（需要 server 端產 PlayAuthToken），
 * 提供 admin 上架前的快速確認資訊：
 *   - 集數封面 / 時長 / 解析度 / 付費狀態 / 上線狀態
 *   - 一鍵複製 vid，方便至 BytePlus VOD 後台搜尋預覽
 *   - 一鍵跳到 BytePlus VOD console 影片列表
 */
export default function VideoPreviewDialog({
  isOpen,
  onClose,
  video,
  dramaId,
  dramaTitle,
}: VideoPreviewDialogProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!video) return null;

  const handleCopy = (field: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const releaseLabel = (() => {
    if (video.release_status === "released" || video.is_released) return "已上線";
    if (video.release_status === "scheduled") return "已排程";
    return "未排程";
  })();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="h-5 w-5" />
            預覽：第 {video.order ?? "?"} 集
            {video.caption || video.name ? (
              <span className="text-muted-foreground font-normal text-sm">
                · {video.caption || video.name}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 封面預覽暫時隱藏：BytePlus image domain 缺 SSL cert，cover URL 無法載入。
              cover_url 資料仍存在 Firestore，未來 SSL 修好可恢復此區塊。 */}

          {/* 元資訊 */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="集數" value={video.order ?? "—"} />
            <Field label="時長" value={formatDuration(video.duration)} />
            <Field
              label="解析度"
              value={
                video.width && video.height
                  ? `${video.width} × ${video.height}`
                  : "—"
              }
            />
            <Field
              label="付費"
              value={
                video.vip ? (
                  <Badge className="bg-amber-500 text-white hover:bg-amber-600">
                    🔒 付費
                  </Badge>
                ) : (
                  <Badge variant="success">免費</Badge>
                )
              }
            />
            <Field
              label="上線狀態"
              value={
                <Badge
                  variant={
                    releaseLabel === "已上線"
                      ? "success"
                      : releaseLabel === "已排程"
                      ? "info"
                      : "secondary"
                  }
                >
                  {releaseLabel}
                </Badge>
              }
            />
            <Field
              label="上傳狀態"
              value={
                video.upload_status ? (
                  <Badge
                    variant={
                      video.upload_status === "completed"
                        ? "success"
                        : video.upload_status === "failed"
                        ? "danger"
                        : "info"
                    }
                  >
                    {video.upload_status}
                  </Badge>
                ) : (
                  "—"
                )
              }
            />
          </div>

          {/* 影片 ID */}
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="text-xs text-muted-foreground mb-2">
              BytePlus 影片 ID (vid)
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono bg-background rounded px-2 py-1.5 truncate">
                {video.vid || "—"}
              </code>
              {video.vid && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopy("vid", video.vid)}
                >
                  {copiedField === "vid" ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" />
                      已複製
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      複製
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              劇集：<span className="font-mono">{dramaId}</span>
              {dramaTitle ? <> · {dramaTitle}</> : null}
            </div>
          </div>

          {/* 預覽說明 */}
          <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 p-3 flex gap-2">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div className="text-xs text-blue-900 dark:text-blue-100 space-y-1">
              <p>
                <strong>如何預覽影片內容：</strong>
              </p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1">
                <li>點下方「打開 BytePlus 後台」，登入 BytePlus VOD console。</li>
                <li>在影片列表用上方複製的 vid 搜尋該影片。</li>
                <li>進入詳情頁可線上播放、檢查多畫質與字幕。</li>
              </ol>
              <p className="pt-1">
                上架後也能直接在 Reel+ App 看到實際播放效果。
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            asChild
          >
            <a
              href="https://console.byteplus.com/vod/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              打開 BytePlus 後台
            </a>
          </Button>
          <Button type="button" onClick={onClose}>
            關閉
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded border bg-muted/20 px-3 py-2">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
